// Notizen/Kommentare für eine Aufgabe — eigenständiges Feld, getrennt vom
// Aktivitäts-Feed (UpdatesFeed). Neueste zuerst; jede Notiz zeigt Ersteller +
// Datum/Uhrzeit; der Ersteller kann seine Notizen nachträglich bearbeiten
// (dann erscheint ein „bearbeitet"-Vermerk) oder löschen.

import { useEffect, useState } from "preact/hooks";
import { Pencil, Trash2, Check, X } from "lucide-preact";
import type { TaskNoteItem } from "@juuplan/shared";
import {
  createTaskNote,
  deleteTaskNote,
  fetchTaskNotes,
  updateTaskNote,
  createPersonalTaskNote,
  deletePersonalTaskNote,
  fetchPersonalTaskNotes,
  updatePersonalTaskNote,
} from "../store/project";
import { useMe } from "../lib/auth";
import { LinkifiedText } from "../lib/linkify";
import { toastError } from "../lib/toast";
import { confirmDialog } from "../lib/confirm";
import { UserAvatar } from "./UserAvatar";
import { BotBadgeFor, BotOwnerNote } from "./BotBadge";

import { Button } from "./ui/Button";

// Entweder taskId (Projekt-Aufgabe) ODER personalTaskId (Aufgabenliste).
interface Props {
  taskId?: number;
  personalTaskId?: number;
  readOnly?: boolean;
}

// Festes Datum + Uhrzeit, z. B. „09.07.2026, 14:32".
function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function NotesSection(props: Props): preact.JSX.Element {
  const { readOnly = false } = props;
  // Genau eine id ist gesetzt; auf die passende API abbilden.
  const isPersonal = props.personalTaskId !== undefined;
  const id = (props.taskId ?? props.personalTaskId) as number;
  const notesApi = isPersonal
    ? {
        fetch: fetchPersonalTaskNotes,
        create: createPersonalTaskNote,
        update: updatePersonalTaskNote,
        del: deletePersonalTaskNote,
      }
    : {
        fetch: fetchTaskNotes,
        create: createTaskNote,
        update: updateTaskNote,
        del: deleteTaskNote,
      };
  const me = useMe();
  const [items, setItems] = useState<TaskNoteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  // Der stumme Reject-Handler liess einen fehlgeschlagenen Abruf wie „Noch
  // keine Notizen" aussehen. api() toastet nur Mutationen, dieser GET nicht.
  const [loadError, setLoadError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = (): void => setReloadTick((t) => t + 1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    void notesApi.fetch(id).then(
      (rows) => {
        if (cancelled) return;
        setItems(rows);
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setLoadError(true);
        setLoading(false);
        toastError("Notizen konnten nicht geladen werden.");
      }
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `reloadTick` haengt drin, damit „Erneut laden" den Abruf neu startet.
  }, [id, reloadTick]);

  const submit = async (): Promise<void> => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const created = await notesApi.create(id, { body: trimmed });
      // Neueste zuerst → vorne einfügen.
      setItems((prev) => [created, ...prev]);
      setBody("");
    } catch {
      // api.ts hat bereits getoastet; Composer-Inhalt bleibt erhalten.
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (note: TaskNoteItem): void => {
    setEditingId(note.id);
    setEditBody(note.body);
  };

  const cancelEdit = (): void => {
    setEditingId(null);
    setEditBody("");
  };

  const saveEdit = async (note: TaskNoteItem): Promise<void> => {
    const trimmed = editBody.trim();
    if (!trimmed) return;
    if (trimmed === note.body) {
      cancelEdit();
      return;
    }
    setSavingEdit(true);
    try {
      const updated = await notesApi.update(id, note.id, { body: trimmed });
      setItems((prev) => prev.map((n) => (n.id === note.id ? updated : n)));
      cancelEdit();
    } catch {
      // api.ts hat bereits getoastet.
    } finally {
      setSavingEdit(false);
    }
  };

  const doDelete = async (note: TaskNoteItem): Promise<void> => {
    const ok = await confirmDialog({
      title: "Notiz löschen",
      message: "Notiz wirklich löschen?",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await notesApi.del(id, note.id);
      setItems((prev) => prev.filter((n) => n.id !== note.id));
    } catch {
      toastError("Konnte Notiz nicht löschen.");
    }
  };

  return (
    <section>
      <h3 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2 flex items-center gap-2">
        Notizen / Kommentare
        {items.length > 0 && (
          <span class="text-label font-medium normal-case tracking-normal text-[color:var(--color-on-surface-variant)]">
            {items.length}
          </span>
        )}
      </h3>

      {!readOnly && (
        <div class="mb-3">
          <textarea
            value={body}
            onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void submit();
              }
            }}
            rows={3}
            placeholder="Notiz schreiben… (Cmd/Strg+Enter sendet)"
            class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-shadow resize-y"
            aria-label="Neue Notiz schreiben"
          />
          <div class="mt-2 flex justify-end">
            <Button
              variant="primary"
              size="md"
              type="button"
              onClick={() => void submit()}
              disabled={!body.trim() || submitting}
            >
              {submitting ? "Speichere…" : "Notiz hinzufügen"}
            </Button>
          </div>
        </div>
      )}

      {loading && items.length === 0 ? (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Lädt…
        </p>
      ) : loadError && items.length === 0 ? (
        <p class="text-xs text-[color:var(--color-error)]">
          Notizen konnten nicht geladen werden.{" "}
          <button type="button" onClick={reload} class="underline">Erneut laden</button>
        </p>
      ) : items.length === 0 ? (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Noch keine Notizen.
        </p>
      ) : (
        <ul class="flex max-h-[20rem] flex-col gap-2 overflow-y-auto pr-1">
          {items.map((note) => {
            const own = me.user?.id === note.user_id;
            const editing = editingId === note.id;
            return (
              <li
                key={note.id}
                class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-3 py-2 text-xs"
              >
                <header class="flex items-center gap-2 mb-1">
                  <UserAvatar
                    userId={note.user_id}
                    size={20}
                    name={note.author.name}
                    avatarUrl={note.author.avatar_url}
                    isBot={note.author.is_bot}
                    botOwnerId={note.author.bot_owner_id}
                  />
                  <span class="font-medium text-[color:var(--color-on-surface)]">
                    {note.author.name}
                  </span>
                  {/* Aus dem Autor-Payload, nicht aus dem Store: ein später
                      deaktivierter Bot fehlt dort und seine alten Notizen
                      verlören sonst die Kennzeichnung. */}
                  <BotBadgeFor
                    isBot={note.author.is_bot}
                    ownerId={note.author.bot_owner_id}
                  />
                  <span class="text-[color:var(--color-on-surface-variant)]">
                    {fmtDateTime(note.created_at)}
                  </span>
                  {note.edited_at && (
                    <span
                      class="text-label italic text-[color:var(--color-on-surface-variant)]"
                      title={`Bearbeitet am ${fmtDateTime(note.edited_at)}`}
                    >
                      · bearbeitet
                    </span>
                  )}
                  {own && !readOnly && !editing && (
                    <span class="ml-auto flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(note)}
                        class="inline-flex size-7 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-high)] hover:text-[color:var(--color-on-surface)] transition-colors"
                        aria-label="Notiz bearbeiten"
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void doDelete(note)}
                        class="inline-flex size-7 items-center justify-center rounded text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                        aria-label="Notiz löschen"
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </span>
                  )}
                </header>
                <BotOwnerNote
                  isBot={note.author.is_bot}
                  ownerId={note.author.bot_owner_id}
                  className="-mt-1 mb-1 pl-7"
                />

                {editing ? (
                  <div>
                    <textarea
                      value={editBody}
                      onInput={(e) =>
                        setEditBody((e.target as HTMLTextAreaElement).value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          void saveEdit(note);
                        }
                      }}
                      rows={3}
                      class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] resize-y"
                      aria-label="Notiz bearbeiten"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autofocus
                    />
                    <div class="mt-1.5 flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        class="inline-flex items-center gap-1 rounded px-2 py-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-high)] transition-colors"
                      >
                        <X size={14} aria-hidden="true" /> Abbrechen
                      </button>
                      <button
                        type="button"
                        onClick={() => void saveEdit(note)}
                        disabled={!editBody.trim() || savingEdit}
                        class="inline-flex items-center gap-1 rounded bg-[color:var(--color-primary)] px-2 py-1 font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] disabled:opacity-50 transition-colors"
                      >
                        <Check size={14} aria-hidden="true" />
                        {savingEdit ? "Speichere…" : "Speichern"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div class="whitespace-pre-wrap break-words text-[color:var(--color-on-surface)]">
                    <LinkifiedText text={note.body} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
